import React, { useEffect, useState } from 'react';
import apiClient from '../services/apiClient';
import { toast } from 'react-toastify';

interface FieldConfig {
  visible: boolean;
  required?: boolean;
  verify?: 'off' | 'register' | 'profile' | 'login';
  order?: number;
  allowConversionToNormal?: boolean;
}

interface AuthForm {
  id?: number;
  form_type: 'register' | 'login' | 'forgot' | 'reset';
  form_name: string;
  countries: string[];
  fields_config: {
    username?: FieldConfig;
    email: FieldConfig;
    phone: FieldConfig;
    password: FieldConfig;
    otp?: FieldConfig;
    google?: FieldConfig;
    facebook?: FieldConfig;
    kick?: FieldConfig;
    passkey?: FieldConfig;
    custom_fields?: CustomFieldConfig[];
    referral?: FieldConfig;
    recaptcha?: FieldConfig;
  };
}

export type FieldType = 'text' | 'number' | 'checkbox' | 'date';

export interface CustomFieldConfig {
  id: string;
  type: FieldType;
  label: string;
  placeholder?: string;
  visible: boolean;
  required: boolean;
  verify?: 'off' | 'register' | 'profile';
  order?: number;
  link_text?: string;
  link_url?: string;
}

const AuthConfigs: React.FC = () => {
  const [forms, setForms] = useState<AuthForm[]>([]);
  const [loading, setLoading] = useState(false);
  const [activeFormIndex, setActiveFormIndex] = useState<number | null>(null);
  const [isPreviewModalOpen, setIsPreviewModalOpen] = useState(false);

  useEffect(() => {
    fetchForms();
  }, []);

  const fetchForms = async () => {
    try {
      const response = await apiClient.get('/admin/auth-forms');
      if (response.data.status) {
        setForms(response.data.data);
        if (response.data.data.length > 0 && activeFormIndex === null) {
          setActiveFormIndex(0);
        }
      }
    } catch (error) {
      toast.error("Failed to fetch auth forms");
    }
  };

  const saveForm = async () => {
    if (activeFormIndex === null) return;
    const form = forms[activeFormIndex];
    setLoading(true);
    try {
      if (form.id) {
        await apiClient.put(`/admin/auth-forms/${form.id}`, form);
        toast.success("Form updated successfully");
      } else {
        await apiClient.post('/admin/auth-forms', form);
        toast.success("Form created successfully");
        await fetchForms(); // refresh to get the ID
      }
    } catch (error) {
      toast.error("Error saving form");
    } finally {
      setLoading(false);
    }
  };

  const deleteForm = async () => {
    if (activeFormIndex === null) return;
    const form = forms[activeFormIndex];
    if (!form.id) {
      // Just remove from local state if it hasn't been saved yet
      const updated = [...forms];
      updated.splice(activeFormIndex, 1);
      setForms(updated);
      setActiveFormIndex(updated.length > 0 ? 0 : null);
      return;
    }

    if (!window.confirm("Are you sure you want to delete this form?")) return;

    setLoading(true);
    try {
      await apiClient.delete(`/admin/auth-forms/${form.id}`);
      toast.success("Form deleted successfully");
      await fetchForms();
      setActiveFormIndex(0);
    } catch (error) {
      toast.error("Error deleting form");
    } finally {
      setLoading(false);
    }
  };

  const handleFieldChange = (field: string, prop: string, value: any) => {
    if (activeFormIndex === null) return;
    const updated = [...forms];
    // @ts-ignore
    if (!updated[activeFormIndex].fields_config[field]) {
      // @ts-ignore
      updated[activeFormIndex].fields_config[field] = {};
    }
    // @ts-ignore
    updated[activeFormIndex].fields_config[field][prop] = value;
    setForms(updated);
  };

  const handleFormPropChange = (prop: keyof AuthForm, value: any) => {
    if (activeFormIndex === null) return;
    const updated = [...forms];
    // @ts-ignore
    updated[activeFormIndex][prop] = value;
    setForms(updated);
  };

  const handleCountriesChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (activeFormIndex === null) return;
    const value = e.target.value;
    const array = value.split(',').map(c => c.trim()).filter(c => c !== '');
    handleFormPropChange('countries', array.length > 0 ? array : ['ALL']);
  };

  const addNewForm = (type: 'register' | 'login' | 'forgot' | 'reset') => {
    const newForm: AuthForm = {
      form_type: type,
      form_name: `New ${type.charAt(0).toUpperCase() + type.slice(1)} Form`,
      countries: ['ALL'],
      fields_config: {
        username: { visible: true, required: true },
        email: { visible: true, required: type === 'forgot' || type === 'register' || type === 'reset', verify: type === 'register' ? 'register' : 'off', order: 1 },
        phone: { visible: true, required: false, verify: 'off', order: 2 },
        password: { visible: true, required: true, verify: 'off', order: 3 },
        google: { visible: type === 'register' || type === 'login' },
        facebook: { visible: type === 'register' || type === 'login' },
        kick: { visible: type === 'register' || type === 'login' },
        passkey: { visible: type === 'register' || type === 'login' },
        recaptcha: { visible: false, required: false },
        custom_fields: []
      }
    };
    setForms([...forms, newForm]);
    setActiveFormIndex(forms.length); // point to the newly added form
  };

  const activeForm = activeFormIndex !== null ? forms[activeFormIndex] : null;

  // Compute if the active form is the last of its type
  const isLastOfType = activeForm 
    ? forms.filter(f => f.form_type === activeForm.form_type).length <= 1 
    : false;

  return (
    <div className="ml-0 lg:ml-[256px] p-4 sm:p-6 text-white min-h-screen bg-[#151929]">
      <div className="max-w-7xl mx-auto flex flex-col md:flex-row gap-6">
        
        {/* Left Sidebar: Vertical Tabs */}
        <div className="w-full md:w-1/4 bg-slate-900/60 rounded-lg border border-gray-700 flex flex-col overflow-hidden h-fit">
          <div className="p-4 border-b border-gray-700 bg-slate-800/50">
            <h2 className="text-lg font-bold text-white">Forms Configuration</h2>
          </div>
          
          <div className="flex flex-col p-2 space-y-1">
            <h3 className="text-xs font-bold text-gray-500 uppercase px-3 py-2 mt-2">Registration Forms</h3>
            {forms.map((form, idx) => form.form_type === 'register' && (
              <button
                key={idx}
                onClick={() => setActiveFormIndex(idx)}
                className={`text-left px-4 py-2 rounded-md text-sm font-bold transition-colors ${
                  activeFormIndex === idx 
                    ? 'bg-indigo-600 text-white shadow-md' 
                    : 'text-gray-400 hover:bg-slate-800 hover:text-gray-200'
                }`}
              >
                <i className="fas fa-user-plus mr-2"></i>
                {form.form_name}
              </button>
            ))}
            <button 
              onClick={() => addNewForm('register')}
              className="text-left px-4 py-2 mt-1 rounded-md text-sm font-bold text-indigo-400 hover:bg-indigo-600/10 transition-colors border border-dashed border-indigo-500/50"
            >
              + Add Registration Form
            </button>

            <h3 className="text-xs font-bold text-gray-500 uppercase px-3 py-2 mt-4">Login Forms</h3>
            {forms.map((form, idx) => form.form_type === 'login' && (
              <button
                key={idx}
                onClick={() => setActiveFormIndex(idx)}
                className={`text-left px-4 py-2 rounded-md text-sm font-bold transition-colors ${
                  activeFormIndex === idx 
                    ? 'bg-indigo-600 text-white shadow-md' 
                    : 'text-gray-400 hover:bg-slate-800 hover:text-gray-200'
                }`}
              >
                <i className="fas fa-sign-in-alt mr-2"></i>
                {form.form_name}
              </button>
            ))}
            <button 
              onClick={() => addNewForm('login')}
              className="text-left px-4 py-2 mt-1 rounded-md text-sm font-bold text-indigo-400 hover:bg-indigo-600/10 transition-colors border border-dashed border-indigo-500/50"
            >
              + Add Login Form
            </button>

            <h3 className="text-xs font-bold text-gray-500 uppercase px-3 py-2 mt-4">Forgot Password Forms</h3>
            {forms.map((form, idx) => form.form_type === 'forgot' && (
              <button
                key={idx}
                onClick={() => setActiveFormIndex(idx)}
                className={`text-left px-4 py-2 rounded-md text-sm font-bold transition-colors ${
                  activeFormIndex === idx 
                    ? 'bg-indigo-600 text-white shadow-md' 
                    : 'text-gray-400 hover:bg-slate-800 hover:text-gray-200'
                }`}
              >
                <i className="fas fa-question-circle mr-2"></i>
                {form.form_name}
              </button>
            ))}
            <button 
              onClick={() => addNewForm('forgot')}
              className="text-left px-4 py-2 mt-1 rounded-md text-sm font-bold text-indigo-400 hover:bg-indigo-600/10 transition-colors border border-dashed border-indigo-500/50"
            >
              + Add Forgot Form
            </button>

            <h3 className="text-xs font-bold text-gray-500 uppercase px-3 py-2 mt-4">Reset Password Forms</h3>
            {forms.map((form, idx) => form.form_type === 'reset' && (
              <button
                key={idx}
                onClick={() => setActiveFormIndex(idx)}
                className={`text-left px-4 py-2 rounded-md text-sm font-bold transition-colors ${
                  activeFormIndex === idx 
                    ? 'bg-indigo-600 text-white shadow-md' 
                    : 'text-gray-400 hover:bg-slate-800 hover:text-gray-200'
                }`}
              >
                <i className="fas fa-key mr-2"></i>
                {form.form_name}
              </button>
            ))}
            <button 
              onClick={() => addNewForm('reset')}
              className="text-left px-4 py-2 mt-1 mb-4 rounded-md text-sm font-bold text-indigo-400 hover:bg-indigo-600/10 transition-colors border border-dashed border-indigo-500/50"
            >
              + Add Reset Form
            </button>
          </div>
        </div>

        {/* Right Panel: Form Editor */}
        <div className="w-full md:w-3/4">
          {activeForm ? (
            <div className="bg-slate-950/50 border border-gray-700 rounded-lg shadow-lg overflow-hidden">
              <div className="p-4 sm:p-6 border-b border-gray-700 flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 bg-slate-900/40">
                <h3 className="text-lg sm:text-xl font-bold text-white">
                  Edit Form: <span className="text-indigo-400">{activeForm.form_name}</span>
                </h3>
                <div className="flex flex-wrap gap-2 w-full sm:w-auto justify-end">
                  <button
                    onClick={deleteForm}
                    disabled={loading || isLastOfType}
                    className={`px-3 py-1.5 text-xs sm:text-sm font-bold rounded transition-colors border ${
                      isLastOfType 
                        ? 'bg-gray-800 text-gray-500 border-gray-700 cursor-not-allowed' 
                        : 'bg-red-500/10 text-red-500 hover:bg-red-500 hover:text-white border-red-500/20'
                    }`}
                    title={isLastOfType ? "Cannot delete the last form of this type" : "Delete Form"}
                  >
                    <i className="fas fa-trash mr-1"></i> Delete
                  </button>
                  <button
                    onClick={() => setIsPreviewModalOpen(true)}
                    className="px-3 py-1.5 bg-slate-700 hover:bg-slate-600 text-white text-xs sm:text-sm font-bold rounded shadow-lg transition-colors"
                  >
                    <i className="fas fa-eye mr-1"></i> Preview
                  </button>
                  <button
                    onClick={saveForm}
                    disabled={loading}
                    className="px-4 py-1.5 bg-indigo-600 hover:bg-indigo-700 text-white text-xs sm:text-sm font-bold rounded shadow-lg transition-colors"
                  >
                    {loading ? 'Saving...' : 'Save Form'}
                  </button>
                </div>
              </div>

              <div className="p-4 sm:p-6 space-y-6">
                
                {/* Form Metadata */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 p-4 bg-slate-900/60 rounded-md border border-gray-700">
                  <div>
                    <label className="block text-xs font-bold text-gray-400 uppercase mb-2">Form Name</label>
                    <input 
                      type="text"
                      value={activeForm.form_name}
                      onChange={(e) => handleFormPropChange('form_name', e.target.value)}
                      className="w-full bg-slate-950 border-slate-700 shadow-inner text-white text-sm px-4 py-2.5 rounded border focus:outline-none focus:border-indigo-500"
                      placeholder="e.g. India Registration"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-gray-400 uppercase mb-2">
                      Target Countries (Comma separated, or ALL)
                    </label>
                    <input 
                      type="text"
                      value={activeForm.countries.join(', ')}
                      onChange={handleCountriesChange}
                      className="w-full bg-slate-950 border-slate-700 shadow-inner text-white text-sm px-4 py-2.5 rounded border focus:outline-none focus:border-indigo-500"
                      placeholder="IN, US, UK"
                    />
                  </div>
                </div>

                {/* Fields Configuration */}
                <div>
                  <h4 className="text-sm font-bold text-gray-300 uppercase mb-4 mt-2 border-b border-gray-700 pb-2">
                    Fields Setup
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    {(() => {
                      if (activeForm.form_type === 'forgot' || activeForm.form_type === 'reset') return ['email', 'phone', 'password'];
                      if (activeForm.form_type === 'register') return ['username', 'email', 'phone', 'password', 'referral'];
                      return ['username', 'email', 'phone', 'password'];
                    })().map((field) => (
                      <div key={field} className="border p-4 sm:p-5 rounded-lg border-gray-700 bg-slate-800/30 hover:bg-slate-800/50 transition-colors">
                        <div className="flex items-center gap-2 mb-5 pb-3 border-b border-gray-700/50">
                          <i className={`fas ${field === 'email' ? 'fa-envelope' : field === 'phone' ? 'fa-phone' : field === 'password' ? 'fa-lock' : field === 'otp' ? 'fa-key' : 'fa-users'} text-indigo-400`}></i>
                          <h4 className="font-bold text-lg capitalize text-white">{field}</h4>
                        </div>
                        
                        <div className="flex flex-col gap-4">
                          {(field !== 'password' || activeForm.form_type === 'login') && (
                            <>
                              <label className="flex items-center justify-between cursor-pointer group">
                                <span className="text-sm font-medium text-gray-400 group-hover:text-gray-200 transition-colors">Visible in Form</span>
                                <div className="relative">
                                  <input 
                                    type="checkbox" 
                                    // @ts-ignore
                                    checked={activeForm.fields_config[field]?.visible ?? true} 
                                    onChange={(e) => handleFieldChange(field, 'visible', e.target.checked)}
                                    className="sr-only peer"
                                  />
                                  <div className="w-11 h-6 bg-gray-700 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-0.5 after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-indigo-500"></div>
                                </div>
                              </label>

                              <label className="flex items-center justify-between cursor-pointer group">
                                <span className="text-sm font-medium text-gray-400 group-hover:text-gray-200 transition-colors">Is Required</span>
                                <div className="relative">
                                  <input 
                                    type="checkbox" 
                                    // @ts-ignore
                                    checked={activeForm.fields_config[field]?.required ?? false} 
                                    onChange={(e) => handleFieldChange(field, 'required', e.target.checked)}
                                    className="sr-only peer"
                                  />
                                  <div className="w-11 h-6 bg-gray-700 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-0.5 after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-indigo-500"></div>
                                </div>
                              </label>
                            </>
                          )}

                          {(activeForm.form_type === 'register' || activeForm.form_type === 'forgot' || activeForm.form_type === 'reset' || activeForm.form_type === 'login') && (
                            (field === 'email' || field === 'phone') ? (
                              <div className="flex flex-col gap-2 mt-2">
                                <label className="text-xs font-bold text-gray-500 uppercase">Verification Rule</label>
                                <div className="relative">
                                  <select 
                                    className="w-full bg-slate-900 border-slate-700 shadow-inner text-white text-sm px-3 py-2.5 rounded border focus:outline-none focus:border-indigo-500 appearance-none cursor-pointer"
                                    // @ts-ignore
                                    value={activeForm.fields_config[field]?.verify ?? 'off'}
                                    onChange={(e) => handleFieldChange(field, 'verify', e.target.value)}
                                  >
                                    <option value="off">No Verification (Off)</option>
                                    {(activeForm.form_type === 'register' || activeForm.form_type === 'forgot' || activeForm.form_type === 'reset') && (
                                      <option value="register">Verify at Submit (OTP/Link)</option>
                                    )}
                                    {activeForm.form_type === 'register' && (
                                      <option value="profile">Verify Later (in Profile)</option>
                                    )}
                                    {activeForm.form_type === 'login' && (
                                      <option value="login">Verify at Login (OTP)</option>
                                    )}
                                  </select>
                                  <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3 text-gray-400">
                                    <i className="fas fa-chevron-down text-xs"></i>
                                  </div>
                                </div>
                              </div>
                            ) : field === 'password' ? (
                              <>
                                <div className="flex flex-col gap-2 mt-2">
                                  <label className="text-xs font-bold text-gray-500 uppercase">Min Length</label>
                                  <input 
                                    type="number"
                                    // @ts-ignore
                                    value={activeForm.fields_config.password?.minLength ?? 6}
                                    onChange={(e) => handleFieldChange('password', 'minLength', parseInt(e.target.value) || 6)}
                                    className="w-full bg-slate-900 border-slate-700 shadow-inner text-white text-sm px-3 py-2 rounded border focus:outline-none focus:border-indigo-500"
                                  />
                                </div>
                                <label className="flex items-center justify-between cursor-pointer group mt-2">
                                  <span className="text-sm font-medium text-gray-400 group-hover:text-gray-200 transition-colors">Require Uppercase</span>
                                  <div className="relative">
                                    <input 
                                      type="checkbox" 
                                      // @ts-ignore
                                      checked={activeForm.fields_config.password?.requireUppercase ?? false} 
                                      onChange={(e) => handleFieldChange('password', 'requireUppercase', e.target.checked)}
                                      className="sr-only peer"
                                    />
                                    <div className="w-11 h-6 bg-gray-700 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-0.5 after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-indigo-500"></div>
                                  </div>
                                </label>
                                <label className="flex items-center justify-between cursor-pointer group mt-2">
                                  <span className="text-sm font-medium text-gray-400 group-hover:text-gray-200 transition-colors">Require Numbers</span>
                                  <div className="relative">
                                    <input 
                                      type="checkbox" 
                                      // @ts-ignore
                                      checked={activeForm.fields_config.password?.requireNumbers ?? false} 
                                      onChange={(e) => handleFieldChange('password', 'requireNumbers', e.target.checked)}
                                      className="sr-only peer"
                                    />
                                    <div className="w-11 h-6 bg-gray-700 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-0.5 after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-indigo-500"></div>
                                  </div>
                                </label>
                                <label className="flex items-center justify-between cursor-pointer group mt-2">
                                  <span className="text-sm font-medium text-gray-400 group-hover:text-gray-200 transition-colors">Require Special Char</span>
                                  <div className="relative">
                                    <input 
                                      type="checkbox" 
                                      // @ts-ignore
                                      checked={activeForm.fields_config.password?.requireSpecial ?? false} 
                                      onChange={(e) => handleFieldChange('password', 'requireSpecial', e.target.checked)}
                                      className="sr-only peer"
                                    />
                                    <div className="w-11 h-6 bg-gray-700 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-0.5 after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-indigo-500"></div>
                                  </div>
                                </label>
                              </>
                            ) : null
                          )}

                          <div className="flex flex-col gap-2 mt-1">
                            <label className="text-xs font-bold text-gray-500 uppercase">Display Order</label>
                            <input 
                              type="number"
                              // @ts-ignore
                              value={activeForm.fields_config[field]?.order ?? (field === 'email' ? 1 : field === 'phone' ? 2 : 3)}
                              onChange={(e) => handleFieldChange(field, 'order', parseInt(e.target.value) || 1)}
                              className="w-full bg-slate-900 border-slate-700 shadow-inner text-white text-sm px-3 py-2 rounded border focus:outline-none focus:border-indigo-500"
                            />
                          </div>

                        </div>
                      </div>
                    ))}
                    
                    {/* Custom Fields - Only for Registration */}
                    {activeForm.form_type === 'register' && (
                      <div className="border p-4 sm:p-5 rounded-lg border-gray-700 bg-slate-800/30 hover:bg-slate-800/50 transition-colors col-span-1 md:col-span-3">
                        <div className="flex items-center justify-between gap-2 mb-5 pb-3 border-b border-gray-700/50">
                        <div className="flex items-center gap-2">
                          <i className="fas fa-plus-square text-green-400"></i>
                          <h4 className="font-bold text-lg capitalize text-white">Custom Fields & Checkboxes</h4>
                        </div>
                        <button 
                          onClick={() => {
                            const updated = [...forms];
                            // @ts-ignore
                            const currentList = updated[activeFormIndex!].fields_config.custom_fields || [];
                            const newField: CustomFieldConfig = {
                              id: 'field_' + Math.random().toString(36).substr(2, 9),
                              type: 'text',
                              label: 'New Field',
                              placeholder: 'Enter details...',
                              visible: true,
                              required: false,
                            };
                            
                            updated[activeFormIndex!] = {
                              ...updated[activeFormIndex!],
                              fields_config: {
                                ...updated[activeFormIndex!].fields_config,
                                custom_fields: [...currentList, newField]
                              }
                            };
                            setForms(updated);
                          }}
                          className="px-3 py-1 bg-green-600 hover:bg-green-700 text-white text-xs font-bold rounded shadow-lg transition-colors"
                        >
                          + Add Field
                        </button>
                      </div>
                      
                      <div className="flex flex-col gap-4">
                        {(activeForm.fields_config.custom_fields || []).map((cfield, cIdx) => (
                          <div key={cfield.id} className="border border-gray-600 p-4 rounded-md bg-slate-900 flex flex-col gap-3 relative">
                            <button 
                              onClick={() => {
                                const updated = [...forms];
                                const updatedFields = [...(updated[activeFormIndex!].fields_config.custom_fields || [])];
                                updatedFields.splice(cIdx, 1);
                                updated[activeFormIndex!] = {
                                  ...updated[activeFormIndex!],
                                  fields_config: {
                                    ...updated[activeFormIndex!].fields_config,
                                    custom_fields: updatedFields
                                  }
                                };
                                setForms(updated);
                              }}
                              className="absolute top-3 right-3 px-2 py-1 bg-red-500/10 hover:bg-red-500/20 text-red-400 hover:text-red-300 text-xs font-bold rounded flex items-center gap-1 transition-colors border border-red-500/20"
                            >
                              <i className="fas fa-trash-alt"></i> Delete
                            </button>

                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
                              <div>
                                <label className="text-xs font-bold text-gray-400 uppercase">Field Label</label>
                                <input 
                                  type="text"
                                  value={cfield.label}
                                  onChange={(e) => {
                                    const updated = [...forms];
                                    const updatedFields = [...(updated[activeFormIndex!].fields_config.custom_fields || [])];
                                    updatedFields[cIdx] = { ...updatedFields[cIdx], label: e.target.value };
                                    updated[activeFormIndex!] = { ...updated[activeFormIndex!], fields_config: { ...updated[activeFormIndex!].fields_config, custom_fields: updatedFields } };
                                    setForms(updated);
                                  }}
                                  className="w-full bg-slate-950 border-slate-700 text-white text-sm px-3 py-2 rounded mt-1"
                                />
                              </div>
                              <div>
                                <label className="text-xs font-bold text-gray-400 uppercase">Placeholder</label>
                                <input 
                                  type="text"
                                  value={cfield.placeholder || ''}
                                  onChange={(e) => {
                                    const updated = [...forms];
                                    const updatedFields = [...(updated[activeFormIndex!].fields_config.custom_fields || [])];
                                    updatedFields[cIdx] = { ...updatedFields[cIdx], placeholder: e.target.value };
                                    updated[activeFormIndex!] = { ...updated[activeFormIndex!], fields_config: { ...updated[activeFormIndex!].fields_config, custom_fields: updatedFields } };
                                    setForms(updated);
                                  }}
                                  className="w-full bg-slate-950 border-slate-700 text-white text-sm px-3 py-2 rounded mt-1"
                                />
                              </div>
                              <div>
                                <label className="text-xs font-bold text-gray-400 uppercase">Field Type</label>
                                <select 
                                  value={cfield.type}
                                  onChange={(e) => {
                                    const updated = [...forms];
                                    const updatedFields = [...(updated[activeFormIndex!].fields_config.custom_fields || [])];
                                    updatedFields[cIdx] = { ...updatedFields[cIdx], type: e.target.value as FieldType };
                                    updated[activeFormIndex!] = { ...updated[activeFormIndex!], fields_config: { ...updated[activeFormIndex!].fields_config, custom_fields: updatedFields } };
                                    setForms(updated);
                                  }}
                                  className="w-full bg-slate-950 border-slate-700 text-white text-sm px-3 py-2 rounded mt-1"
                                >
                                  <option value="text">Text Input</option>
                                  <option value="number">Number Input</option>
                                  <option value="date">Date Input</option>
                                  <option value="checkbox">Checkbox (e.g. T&C)</option>
                                </select>
                              </div>
                              <div>
                                <label className="text-xs font-bold text-gray-400 uppercase">Display Order</label>
                                <input 
                                  type="number"
                                  value={cfield.order ?? 10}
                                  onChange={(e) => {
                                    const updated = [...forms];
                                    const updatedFields = [...(updated[activeFormIndex!].fields_config.custom_fields || [])];
                                    updatedFields[cIdx] = { ...updatedFields[cIdx], order: parseInt(e.target.value) || 10 };
                                    updated[activeFormIndex!] = { ...updated[activeFormIndex!], fields_config: { ...updated[activeFormIndex!].fields_config, custom_fields: updatedFields } };
                                    setForms(updated);
                                  }}
                                  className="w-full bg-slate-950 border-slate-700 text-white text-sm px-3 py-2 rounded mt-1"
                                />
                              </div>
                              <div className="flex items-center gap-4 mt-6">
                                <label className="flex items-center cursor-pointer gap-2">
                                  <input 
                                    type="checkbox"
                                    checked={cfield.visible}
                                    onChange={(e) => {
                                      const updated = [...forms];
                                      const updatedFields = [...(updated[activeFormIndex!].fields_config.custom_fields || [])];
                                      updatedFields[cIdx] = { ...updatedFields[cIdx], visible: e.target.checked };
                                      updated[activeFormIndex!] = { ...updated[activeFormIndex!], fields_config: { ...updated[activeFormIndex!].fields_config, custom_fields: updatedFields } };
                                      setForms(updated);
                                    }}
                                  /> <span className="text-sm text-gray-300">Visible</span>
                                </label>
                                <label className="flex items-center cursor-pointer gap-2">
                                  <input 
                                    type="checkbox"
                                    checked={cfield.required}
                                    onChange={(e) => {
                                      const updated = [...forms];
                                      const updatedFields = [...(updated[activeFormIndex!].fields_config.custom_fields || [])];
                                      updatedFields[cIdx] = { ...updatedFields[cIdx], required: e.target.checked };
                                      updated[activeFormIndex!] = { ...updated[activeFormIndex!], fields_config: { ...updated[activeFormIndex!].fields_config, custom_fields: updatedFields } };
                                      setForms(updated);
                                    }}
                                  /> <span className="text-sm text-gray-300">Required</span>
                                </label>
                              </div>
                            </div>
                            
                            {cfield.type === 'checkbox' && (
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-2 p-3 bg-slate-800 rounded">
                                <div>
                                  <label className="text-xs font-bold text-gray-400 uppercase">Link Text (Optional)</label>
                                  <input 
                                    type="text"
                                    value={cfield.link_text || ''}
                                    placeholder="e.g. Terms and Conditions"
                                    onChange={(e) => {
                                      const updated = [...forms];
                                      const updatedFields = [...(updated[activeFormIndex!].fields_config.custom_fields || [])];
                                      updatedFields[cIdx] = { ...updatedFields[cIdx], link_text: e.target.value };
                                      updated[activeFormIndex!] = { ...updated[activeFormIndex!], fields_config: { ...updated[activeFormIndex!].fields_config, custom_fields: updatedFields } };
                                      setForms(updated);
                                    }}
                                    className="w-full bg-slate-950 border-slate-700 text-white text-sm px-3 py-2 rounded mt-1"
                                  />
                                </div>
                                <div>
                                  <label className="text-xs font-bold text-gray-400 uppercase">Link URL (Optional)</label>
                                  <input 
                                    type="text"
                                    value={cfield.link_url || ''}
                                    placeholder="e.g. /terms"
                                    onChange={(e) => {
                                      const updated = [...forms];
                                      const updatedFields = [...(updated[activeFormIndex!].fields_config.custom_fields || [])];
                                      updatedFields[cIdx] = { ...updatedFields[cIdx], link_url: e.target.value };
                                      updated[activeFormIndex!] = { ...updated[activeFormIndex!], fields_config: { ...updated[activeFormIndex!].fields_config, custom_fields: updatedFields } };
                                      setForms(updated);
                                    }}
                                    className="w-full bg-slate-950 border-slate-700 text-white text-sm px-3 py-2 rounded mt-1"
                                  />
                                </div>
                              </div>
                            )}

                          </div>
                        ))}
                        {(activeForm.fields_config.custom_fields?.length || 0) === 0 && (
                          <p className="text-sm text-gray-500 italic">No custom fields added yet.</p>
                        )}
                      </div>
                    </div>
                    )}
                    
                    {/* Social Logins */}
                    {(activeForm.form_type === 'register' || activeForm.form_type === 'login') && ['google', 'facebook', 'kick', 'passkey'].map((field) => (
                      <div key={field} className="border p-4 sm:p-5 rounded-lg border-gray-700 bg-slate-800/30 hover:bg-slate-800/50 transition-colors flex flex-col justify-between">
                        <div>
                          <div className="flex items-center gap-2 mb-5 pb-3 border-b border-gray-700/50">
                            <i className={`fab fa-${field === 'passkey' ? 'key' : field} text-${field === 'google' ? 'red' : field === 'passkey' ? 'green' : 'blue'}-500 text-lg`}></i>
                            <h4 className="font-bold text-lg capitalize text-white">{field} Login</h4>
                          </div>
                          <p className="text-xs text-gray-500 mb-4">
                            Enable or disable {field} social login for this specific form.
                          </p>
                        </div>
                        
                        <div className="flex flex-col gap-4">
                          <label className="flex items-center justify-between cursor-pointer group">
                            <span className="text-sm font-medium text-gray-400 group-hover:text-gray-200 transition-colors">Visible in Form</span>
                            <div className="relative">
                              <input 
                                type="checkbox" 
                                // @ts-ignore
                                checked={activeForm.fields_config[field]?.visible ?? true} 
                                onChange={(e) => handleFieldChange(field, 'visible', e.target.checked)}
                                className="sr-only peer"
                              />
                              <div className="w-11 h-6 bg-gray-700 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-0.5 after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-indigo-500"></div>
                            </div>
                          </label>

                          {/* Allow Conversion Toggle for Socials */}
                          <label className="flex items-center justify-between cursor-pointer group">
                            <span className="text-sm font-medium text-gray-400 group-hover:text-gray-200 transition-colors">Allow Convert to Normal Login</span>
                            <div className="relative">
                              <input 
                                type="checkbox" 
                                // @ts-ignore
                                checked={activeForm.fields_config[field]?.allowConversionToNormal ?? false} 
                                onChange={(e) => handleFieldChange(field, 'allowConversionToNormal', e.target.checked)}
                                className="sr-only peer"
                              />
                              <div className="w-11 h-6 bg-gray-700 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-0.5 after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-green-500"></div>
                            </div>
                          </label>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

              </div>

              {/* LIVE PREVIEW SECTION (MODAL) */}
              {isPreviewModalOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4">
                  <div className="w-full max-w-md bg-[#1A232E] rounded-xl text-white shadow-2xl relative overflow-hidden border border-gray-700 flex flex-col">
                    <div className="flex justify-between items-center p-4 border-b border-gray-700">
                      <h4 className="font-bold flex items-center gap-2">
                        <i className="fas fa-eye text-indigo-400"></i> Form Live Preview
                      </h4>
                      <button 
                        onClick={() => setIsPreviewModalOpen(false)}
                        className="text-gray-400 hover:text-white text-xl"
                      >
                        ×
                      </button>
                    </div>
                    
                    <div className="p-6 bg-[#232626]">
                      <div className="text-center mb-6">
                        <p className="text-xl font-bold mb-1">Stay Untamed</p>
                        <p className="text-sm text-gray-400">
                          {activeForm.form_type === 'register' ? 'Sign Up & Get Welcome Bonus' : 
                           activeForm.form_type === 'login' ? 'Sign In' :
                           activeForm.form_type === 'forgot' ? 'Find Your Account' :
                           'Reset Your Password'}
                        </p>
                      </div>

                      <div className="flex flex-col gap-4">
                        {/* Fixed Username */}
                        {activeForm.form_type !== 'reset' && activeForm.form_type !== 'forgot' && (
                          <input 
                            type="text" 
                            disabled 
                            placeholder={activeForm.form_type === 'register' ? "Username" : "Email or Username"} 
                            className="w-full bg-[#1E2121] border border-[#3A4142] rounded-md px-3 py-2 text-sm text-gray-400 placeholder-gray-500 cursor-not-allowed" 
                          />
                        )}

                        {/* Dynamic Fields Sorted by Order */}
                        {[
                          ...(activeForm.form_type === 'register' || activeForm.form_type === 'forgot' || activeForm.form_type === 'reset' ? [{ id: 'email', ...activeForm.fields_config.email }] : []),
                          ...(activeForm.form_type === 'register' || activeForm.form_type === 'forgot' || activeForm.form_type === 'reset' ? [{ id: 'phone', ...activeForm.fields_config.phone }] : []),
                          { id: 'password', ...activeForm.fields_config.password },
                          ...(activeForm.form_type === 'register' ? (activeForm.fields_config.custom_fields || []) : []),
                          ...(activeForm.form_type === 'register' ? [{ id: 'referral', label: 'Referral Code', ...activeForm.fields_config.referral }] : [])
                        ]
                          .filter(f => f.visible !== false)
                          .sort((a, b) => (a.order || 10) - (b.order || 10))
                          .map((f: any, idx) => {
                            if (f.id === 'email') return <input key={idx} type="text" disabled placeholder="Email Address" className="w-full bg-[#1E2121] border border-[#3A4142] rounded-md px-3 py-2 text-sm text-gray-400 placeholder-gray-500 cursor-not-allowed" />;
                            if (f.id === 'phone') return <input key={idx} type="text" disabled placeholder="Phone Number" className="w-full bg-[#1E2121] border border-[#3A4142] rounded-md px-3 py-2 text-sm text-gray-400 placeholder-gray-500 cursor-not-allowed" />;
                            if (f.id === 'password') return <input key={idx} type="password" disabled placeholder={activeForm.form_type === 'reset' || activeForm.form_type === 'forgot' ? "New Password" : "Password"} className="w-full bg-[#1E2121] border border-[#3A4142] rounded-md px-3 py-2 text-sm text-gray-400 placeholder-gray-500 cursor-not-allowed" />;
                            if (f.id === 'referral') return <input key={idx} type="text" disabled placeholder={`Referral Code ${f.required ? '*' : '(Optional)'}`} className="w-full bg-[#1E2121] border border-[#3A4142] rounded-md px-3 py-2 text-sm text-gray-400 placeholder-gray-500 cursor-not-allowed" />;
                            
                            // Custom Field
                            if (f.type === 'checkbox') {
                              return (
                                <div key={idx} className="flex items-center gap-2 mt-1">
                                  <input type="checkbox" disabled className="rounded border-gray-600 bg-gray-700 cursor-not-allowed" />
                                  <label className="text-xs text-gray-400 cursor-not-allowed">
                                    {f.label}{' '}
                                    {f.link_text && <span className="text-[#83EA77]">{f.link_text}</span>}
                                  </label>
                                </div>
                              );
                            } else {
                              return (
                                <input 
                                  key={idx} 
                                  type={f.type === 'date' ? 'date' : 'text'} 
                                  disabled 
                                  placeholder={f.label} 
                                  className="w-full bg-[#1E2121] border border-[#3A4142] rounded-md px-3 py-2 text-sm text-gray-400 placeholder-gray-500 cursor-not-allowed" 
                                />
                              );
                            }
                          })}

                        {/* Submit Button */}
                        <button disabled className="w-full py-2.5 rounded-md font-bold text-white mt-2 opacity-80 cursor-not-allowed bg-blue-600">
                          {activeForm.form_type === 'register' ? 'Register' : 'Sign In'}
                        </button>
                      </div>

                      {/* Social Logins */}
                      {(activeForm.fields_config.google?.visible || activeForm.fields_config.facebook?.visible) && (
                        <div className="text-center mt-4">
                          <p className="text-xs text-gray-500 mb-2">OR</p>
                          <div className="flex flex-col gap-2 justify-center mt-2">
                            <button disabled className="w-full bg-[#2b2f33] border-none text-white p-3 rounded text-sm font-bold cursor-not-allowed flex items-center justify-center gap-2 opacity-70">
                              <i className="fas fa-fingerprint"></i> Sign In with passkey
                            </button>
                            <div className="flex justify-center gap-3 mt-2">
                              {activeForm.fields_config.google?.visible && (
                                <div className="w-10 h-10 rounded-full bg-[#2b2f33] flex items-center justify-center opacity-50"><i className="fab fa-google"></i></div>
                              )}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

            </div>
          ) : (
            <div className="bg-slate-900/60 border border-gray-700 rounded-lg shadow-lg flex flex-col items-center justify-center p-12 h-full min-h-[400px]">
              <i className="fas fa-clipboard-list text-6xl text-gray-600 mb-4"></i>
              <h3 className="text-xl font-bold text-white mb-2">No Form Selected</h3>
              <p className="text-gray-400 text-center max-w-md">
                Select a form from the sidebar to edit its configurations, or create a new form using the "+ Add Form" buttons.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AuthConfigs;
